import React, { useState, useRef } from 'react';

interface ChatInputProps {
  onSend: (content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string }) => Promise<void>;
  disabled?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled }) => {
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = async () => {
    if (!message.trim() && !isUploading) return;

    try {
      await onSend(message.trim());
      setMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic validation
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File too large. Maximum size is 10MB.');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only images (JPG, PNG, WebP, GIF) and videos (MP4, MOV) are allowed.');
      return;
    }

    setIsUploading(true);
    try {
      // Upload media to server
      const formData = new FormData();
      formData.append('media', file);

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;
      const response = await fetch(`${API_BASE_URL}/api/messages/upload-media`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();

      if (data.success) {
        // Send message with media
        await onSend('', {
          mediaType: data.data.mediaType,
          mediaUrl: data.data.mediaUrl,
          thumbnailUrl: data.data.thumbnailUrl
        });
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Failed to upload media:', error);
      alert('Failed to upload media. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="card" style={{ borderRadius: '0', borderTop: '1px solid var(--border-color)' }}>
      <div className="card-content" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,video/*"
            style={{ display: 'none' }}
          />
          <button
            className="icon-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isUploading}
            title="Attach media"
            style={{
              color: 'var(--text-secondary)',
              padding: '8px'
            }}
          >
            <span className="material-icons">attach_file</span>
          </button>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={disabled || isUploading}
            rows={1}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              resize: 'none',
              fontFamily: 'inherit',
              fontSize: '14px',
              color: 'var(--text-primary)',
              background: 'var(--background-paper)',
              minHeight: '40px',
              maxHeight: '120px',
              overflowY: 'auto'
            }}
          />

          <button
            className="btn"
            onClick={handleSend}
            disabled={disabled || isUploading || !message.trim()}
            style={{
              padding: '10px 20px',
              minWidth: '80px',
              background: message.trim() ? 'var(--primary-color)' : 'var(--border-color)',
              color: message.trim() ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '500',
              cursor: message.trim() && !disabled && !isUploading ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
          >
            {isUploading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};
